import { HttpErrorResponse } from '@angular/common/http';

export interface HttpErrorMessageKeys
{
  forbidden: string;
  badRequestByErrorCode?: Record<string, string>;
  badRequestByMessage?: Record<string, string>;
  conflict: string;
  conflictByErrorCode?: Record<string, string>;
  network: string;
  fallback: string;
}

const DEFAULT_ERROR_KEYS: HttpErrorMessageKeys = {
  forbidden: 'error.forbiddenResource',
  conflict: 'error.generic',
  network: 'error.network',
  fallback: 'error.generic'
};

export function resolveHttpErrorMessageKey(
  error: unknown,
  overrides?: Partial<HttpErrorMessageKeys>
): string
{
  const keys: HttpErrorMessageKeys = {
    ...DEFAULT_ERROR_KEYS,
    ...overrides
  };

  if (!(error instanceof HttpErrorResponse))
  {
    return keys.fallback;
  }

  if (error.status === 403)
  {
    return keys.forbidden;
  }

  if (error.status === 400)
  {
    const errorCode = extractHttpErrorCode(error);
    if (errorCode && keys.badRequestByErrorCode?.[errorCode])
    {
      return keys.badRequestByErrorCode[errorCode];
    }

    const message = extractHttpErrorMessage(error);
    if (message && keys.badRequestByMessage?.[message])
    {
      return keys.badRequestByMessage[message];
    }

    return keys.fallback;
  }

  if (error.status === 409)
  {
    const errorCode = extractHttpErrorCode(error);
    if (errorCode && keys.conflictByErrorCode?.[errorCode])
    {
      return keys.conflictByErrorCode[errorCode];
    }
    return keys.conflict;
  }

  if (error.status === 0)
  {
    return keys.network;
  }

  return keys.fallback;
}

function extractHttpErrorCode(error: HttpErrorResponse): string | null
{
  const payload = error.error;
  if (!payload || typeof payload !== 'object')
  {
    return null;
  }

  const candidate = (payload as Record<string, unknown>)['errorCode'];
  return typeof candidate === 'string' && candidate.trim().length > 0
    ? candidate.trim()
    : null;
}

function extractHttpErrorMessage(error: HttpErrorResponse): string | null
{
  const payload = error.error;
  if (!payload || typeof payload !== 'object')
  {
    return null;
  }

  const candidate = (payload as Record<string, unknown>)['message'];
  if (typeof candidate === 'string' && candidate.trim().length > 0)
  {
    return candidate.trim();
  }

  if (Array.isArray(candidate))
  {
    const firstMessage = candidate.find((item): item is string =>
      typeof item === 'string' && item.trim().length > 0
    );

    return firstMessage?.trim() ?? null;
  }

  return null;
}
